import type { ITagProps } from "@fluidattacks/design";
import { TableButton, Tag, Toggle } from "@fluidattacks/design";
import type { CellContext } from "@tanstack/react-table";
import { isNull } from "lodash";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import type { IGitRootData, IGitRootEventsData } from "../types";
import { includeTagsFormatter } from "utils/format-helpers";

const SCOPE_PATH_LENGTH = 6;

const statusVariants: Record<string, ITagProps> = {
  CLONING: {
    icon: "spinner-third",
    priority: "medium",
    tagLabel: "Cloning",
    variant: "warning",
  },
  FAILED: {
    icon: "circle-xmark",
    priority: "low",
    tagLabel: "Failed",
    variant: "error",
  },
  OK: {
    icon: "circle-check",
    priority: "low",
    tagLabel: "Cloned",
    variant: "success",
  },
  QUEUED: {
    icon: "clock",
    priority: "low",
    tagLabel: "Queued",
    variant: "warning",
  },
  UNKNOWN: {
    icon: "circle-dashed",
    tagLabel: "Unknown",
    variant: "inactive",
  },
};

const StatusChangeFormatter = (
  cell: CellContext<IGitRootEventsData, unknown>,
  canUpdate: boolean,
  onUpdate: (row: Record<string, string>) => void,
): JSX.Element => {
  const row = cell.row.original;

  const handleOnChange = useCallback((): void => {
    onUpdate(row as unknown as Record<string, string>);
  }, [onUpdate, row]);

  const isActive = !("state" in row) || row.state.toUpperCase() !== "INACTIVE";

  return canUpdate ? (
    <Toggle
      defaultChecked={isActive}
      leftDescription={{ off: "Inactive", on: "Active" }}
      leftDescriptionMinWidth={"50px"}
      name={"root-status"}
      onChange={handleOnChange}
    />
  ) : (
    <Tag
      priority={"low"}
      tagLabel={isActive ? "Active" : "Inactive"}
      variant={isActive ? "success" : "inactive"}
    />
  );
};

const CloningStatusFormatter = (
  cell: CellContext<IGitRootEventsData, unknown>,
): JSX.Element => {
  const { t } = useTranslation();

  const isInactive = cell.row.original.state === "INACTIVE";
  const cloningStatus = Object.keys(statusVariants).includes(
    cell.row.original.cloningStatus.status,
  )
    ? (cell.row.original.cloningStatus.status as keyof typeof statusVariants)
    : "UNKNOWN";

  const currentUrl: string = window.location.href;
  const modifiedUrl: string = currentUrl.endsWith("/scope")
    ? currentUrl.slice(0, -SCOPE_PATH_LENGTH)
    : currentUrl;

  const customTag = isInactive ? (
    <Tag icon={"power-off"} tagLabel={"Inactive"} variant={"inactive"} />
  ) : (
    <Tag {...statusVariants[cloningStatus]} />
  );

  const eventTag =
    cell.row.original.eventId === undefined ? undefined : (
      <Tag
        href={`${modifiedUrl}/events/${cell.row.original.eventId}/description`}
        icon={"hexagon-exclamation"}
        priority={"medium"}
        tagLabel={t("group.scope.git.repo.status.value.events")}
        variant={"error"}
      />
    );

  return includeTagsFormatter({ customTag, text: eventTag });
};

const ActionsFormatter = (
  cell: CellContext<IGitRootEventsData, unknown>,
  changeFunction: (arg: IGitRootData) => void,
  loading: boolean,
): JSX.Element => {
  const row = cell.row.original;
  const { t } = useTranslation();

  const handleOnChange = useCallback(
    (ev: React.SyntheticEvent): void => {
      ev.stopPropagation();
      changeFunction(row);
    },
    [changeFunction, row],
  );

  return (
    <TableButton
      disabled={
        row.state !== "ACTIVE" ||
        isNull(row.credentials) ||
        (!isNull(row.credentials) && row.credentials.name === "") ||
        loading
      }
      icon={"sync"}
      id={"gitRootSync"}
      label={t("buttons.update")}
      onClick={handleOnChange}
      variant={"submit"}
    />
  );
};

export { StatusChangeFormatter, CloningStatusFormatter, ActionsFormatter };
